import React, { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import AlertContext from "../../../../Context/Alert/AlertContext";
import axios from "axios";
import { BaseURL } from "../../../../Files/BaseURL";
import ThemeContext from "../../../../Context/theme/ThemeContext";

const MarketPlaceOverview = () => {
  const { theme } = useContext(ThemeContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);

  const alertContext = useContext(AlertContext);
  const { showAlert } = alertContext;

  const [formData, setFormData] = useState(null);

  useEffect(() => {
    try {
      const { formData } = location.state || {};
      if (!formData) {
        showAlert("No form data found", "danger");
        navigate("/user-dashboard/profile");
        return;
      }
      setFormData(formData);
    } catch (error) {
      showAlert("Error loading data", "danger");
    } finally {
      setIsInitializing(false);
    }
  }, [location.state]);

  const handlePublish = async () => {
    try {
      setIsLoading(true);
      const authToken = localStorage.getItem("p@token");

      if (!authToken) {
        showAlert("Authentication failed", "danger");
        navigate("/signin");
        return;
      }

      // Validate required fields
      if (!formData?.Category || !formData?.SubCategory) {
        showAlert("Category and SubCategory are required", "warning");
        setIsLoading(false);
        return;
      }

      const data = new FormData();
      data.append("title", formData.Title || "");
      data.append("category", formData.Category); // This should be an ObjectId
      data.append("subCategory", formData.SubCategory); // This should be an ObjectId
      data.append(
        "keywords",
        Array.isArray(formData.Keywords) ? formData.Keywords.join(",") : ""
      );

      // Validate package data
      if (!formData.Pacakage?.Title || !formData.Pacakage?.Description) {
        showAlert("Package title and description are required", "warning");
        setIsLoading(false);
        return;
      }

      data.append(
        "package",
        JSON.stringify({
          Title: formData.Pacakage.Title,
          Description: formData.Pacakage.Description,
          Delivery: formData.Pacakage.Delivery,
          Concept: formData.Pacakage.Concept,
          Price: formData.Pacakage.Price,
        })
      );

      // Filter and validate images
      const filteredImagesURL =
        formData.Images?.filter(
          (image) => image && !image.preview?.includes("blob:")
        ) || [];

      data.append("imagesURL", JSON.stringify(filteredImagesURL));

      // Append only valid image files
      if (formData.Images?.length) {
        formData.Images.forEach((image) => {
          if (image?.file instanceof File) {
            data.append("images", image.file);
          }
        });
      }

      const editId = new URLSearchParams(location.search).get("editid");
      const url = editId
        ? `${BaseURL}/api/portfolio/marketplace/${editId}`
        : `${BaseURL}/api/portfolio/marketplace/markeplacecreation`;

      const response = await axios({
        method: editId ? "put" : "post",
        url,
        data,
        headers: {
          "auth-token": authToken,
          "Content-Type": "multipart/form-data",
        },
      });

      if (response.status === 200) {
        showAlert(
          editId
            ? "Marketplace item updated successfully!"
            : "Marketplace item created successfully!",
          "success"
        );
        navigate("/user-dashboard/profile/profile_success");
      } else {
        throw new Error("Failed to save marketplace item");
      }
    } catch (error) {
      console.error("API Error:", error.response?.data); // For debugging
      showAlert(
        error.response?.data?.message || "Error saving marketplace item",
        "danger"
      );
      setIsLoading(false);
    }
  };

  if (isInitializing) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primarygreen"></div>
      </div>
    );
  }

  return (
    <div className="flex items-center font-pop flex-col">
      <div
        className={`rounded-2xl py-6 px-6 my-6 w-[95%] md:w-[70%] xl:w-[40%] flex flex-col gap-4
            ${theme === "dark" ? "bg-black text-white" : "bg-white"}`}
      >
        <h1 className="font-semibold text-xl text-center">Overview</h1>
        <div className="flex flex-col gap-4">
          <div className="flex font-pop flex-col gap-2">
            <p
              className={`text-base text-lightGrey font-light
                        ${theme === "dark" && "text-white"}  `}
            >
              Title:
            </p>
            <h3 className="text-lg font-medium truncate">{formData?.Title}</h3>
          </div>
          <div className="flex font-pop flex-col gap-2">
            <p
              className={`text-base text-lightGrey font-light
                         ${theme === "dark" && "text-white"} `}
            >
              Description:
            </p>
            <p className="text-base font-normal truncate">
              {formData?.Pacakage?.Description}
            </p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
            <div className="flex font-pop flex-col gap-2">
              <p
                className={`text-base text-lightGrey font-light
                             ${theme === "dark" && "text-white"} `}
              >
                Delivery time:
              </p>
              <p className="text-base font-normal">
                {formData?.Pacakage?.Delivery}
              </p>
            </div>
            <div className="flex font-pop flex-col gap-2">
              <p
                className={`text-base text-lightGrey font-light
                             ${theme === "dark" && "text-white"} `}
              >
                Number of concepts:
              </p>
              <p className="text-base font-normal">
                {formData?.Pacakage?.Concept}
              </p>
            </div>
            <div className="flex font-pop flex-col gap-2">
              <p
                className={`text-base text-lightGrey font-light
                             ${theme === "dark" && "text-white"} `}
              >
                Price:
              </p>
              <h3 className="text-lg font-medium">
                ${formData?.Pacakage?.Price}
              </h3>
            </div>
          </div>
          <div className="flex items-center gap-2 justify-center w-full">
            {formData?.Images.map((image, index) => (
              <div key={index} className="relative">
                <img
                  src={image.preview}
                  alt={`Preview ${index}`}
                  className="rounded-lg max-h-40"
                />
              </div>
            ))}
          </div>
          <button
            className={`my-4 px-2 md:px-6 bg-green-500 hover:bg-transparent hover:bg-white-500 font-medium hover:text-black font-pop text-sm md:text-lg py-2 md:py-3 border border-black-500 hover:border-green-500 rounded-full text-white
             ${theme === "dark" ? "hover:text-white" : "hover:text-black"}
             ${isLoading ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
            onClick={handlePublish}
            disabled={isLoading}
          >
            {isLoading ? (
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              </div>
            ) : new URLSearchParams(location.search).get("editid") ? (
              "Update"
            ) : (
              "Publish"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MarketPlaceOverview;
